import { ExtrudeCutDefinitionSnapshotBuilder } from '../../runtime/ExtrudeCutDefinitionSnapshotBuilder';
import { cloneExtrudeEndTarget } from '../../../utils/feature';
import { CutExtrudeDefinitionCoordinator } from './CutExtrudeDefinitionCoordinator';
import type { ExtrudeFeatureEditDefinition } from './ExtrudeFeatureEditDefinition';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/extrude 层；
 * 2. 负责把宿主传入的拉伸编辑定义净化为纯领域快照；
 * 3. 该类只处理定义净化，不承担默认值、命令映射或特征定义构建职责。
 *
 * 文件职责：
 * 1. 收口 `Vue Proxy`、浅拷贝对象与切除专属快照的净化；
 * 2. 统一复制方向、薄壁与切除定义，阻断运行时对象泄漏；
 * 3. 在 cut-extrude 下统一调用切除协调器，确保方向镜像语义始终一致。
 */
export class ExtrudeFeatureEditDefinitionSanitizer {
    /**
     * 净化拉伸编辑定义。
     *
     * 设计说明：
     * 1. web-client 可能传入响应式代理对象，定义层不能继续持有这些对象；
     * 2. 净化后的结果必须只包含纯数据字段，供 preview / commit / rebuild 复用；
     * 3. 切除定义需要在这里统一完成镜像同步，避免上层再散落状态修补逻辑。
     *
     * @param definition 原始编辑定义。
     * @returns 纯数据编辑定义快照。
     */
    public static sanitize(definition: ExtrudeFeatureEditDefinition): ExtrudeFeatureEditDefinition {
        const sanitizedDefinition: ExtrudeFeatureEditDefinition = {
            featureKind: definition.featureKind,
            operation: definition.operation,
            direction1: this.cloneDirection(definition.direction1),
            direction2: this.cloneDirection(definition.direction2),
            thinFeature: this.cloneThinFeature(definition.thinFeature),
            mergeResult: definition.mergeResult,
            selectedContoursInputActive: definition.selectedContoursInputActive,
            cutOptions: ExtrudeCutDefinitionSnapshotBuilder.clone(definition.cutOptions),
        };
        if (sanitizedDefinition.featureKind === 'boss-extrude' && sanitizedDefinition.direction1.endCondition === 'mid_plane') {
            // 活动方向在稳定引用解析前统一收口，隐藏的旧D2既不求值也不保留强依赖。
            sanitizedDefinition.direction2.enabled = false;
        }

        return sanitizedDefinition.featureKind === 'cut-extrude' || sanitizedDefinition.operation === 'cut'
            ? CutExtrudeDefinitionCoordinator.synchronizeDefinition(sanitizedDefinition)
            : sanitizedDefinition;
    }

    /**
     * 复制单个方向定义。
     *
     * @param direction 原始方向定义。
     * @returns 纯数据方向定义。
     */
    private static cloneDirection(direction: ExtrudeDirectionDefinition): ExtrudeDirectionDefinition {
        return {
            ...direction,
            target: cloneExtrudeEndTarget(direction.target),
            draft: {
                ...direction.draft,
            },
        };
    }

    /**
     * 复制薄壁定义。
     *
     * @param thinFeature 原始薄壁定义。
     * @returns 纯数据薄壁定义。
     */
    private static cloneThinFeature(
        thinFeature: ExtrudeThinFeatureDefinition,
    ): ExtrudeThinFeatureDefinition {
        return {
            ...thinFeature,
        };
    }
}
